import type { ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import CharacterCount from './CharacterCount';
import type { InputBaseProps, InputRenderProps } from './InputBase';
import InputBase from './InputBase';

//  TODO: this is nuts, lets do an omit instead...
export type TextInputProps =
  Pick<InputBaseProps,
  | 'autoFocus'
  | 'id'
  | 'isDisabled'
  | 'isCopyable'
  | 'isReadOnly'
  | 'isRequired'
  | 'label'
  | 'maxLength'
  | 'onChange'
  | 'onKeyDown'
  | 'placeholder'
  | 'size'
  | 'testId'
  | 'title'
  | 'type'
  | 'value'
  | 'name'
  > & {
    /**
     * If the char count should be displayed.
     */
    showCharCount?: boolean;
    /**
     * element to put at the end of the text input
     */
    suffix?: ReactNode | string;
    /**
     * Text input values should always be strings
     */
    value?: string;
    /**
     * Optional validation regexp for value
     */
    pattern?: RegExp;
    /**
     * Optional: Toggle browser spellchecking of input
     */
    spellCheck?: boolean;
  };

/**
 * A generic, controlled text input for FOSSA Form elements.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({
  autoFocus,
  isDisabled,
  isRequired,
  isCopyable,
  isReadOnly,
  label,
  maxLength,
  onChange,
  onKeyDown,
  placeholder,
  showCharCount,
  size = 'medium',
  suffix,
  testId,
  title,
  type = 'text',
  value,
  pattern,
  spellCheck,
  name,
}: TextInputProps, ref: Ref<HTMLInputElement>) => (
  <InputBase
    autoFocus={autoFocus}
    isDisabled={isDisabled}
    isRequired={isRequired}
    isCopyable={isCopyable}
    isReadOnly={isReadOnly}
    label={label}
    maxLength={maxLength}
    onChange={onChange}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    size={size}
    testId={testId}
    title={title}
    type={type}
    value={value}
    name={name}
  >
    {(inputBaseProps: InputRenderProps) => {
      const inputToDisplay = suffix
        ? (
          <div className="fossa-form__text-input__wrapper">
            {/* Suffix placed first so we can target the input with a sibling selector */}
            <div className="fossa-form__text-input__wrapper__suffix">
              {suffix}
            </div>
            <input
              ref={ref}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...inputBaseProps}
            />
          </div>
          ) : (
            <input
              pattern={pattern?.source}
              ref={ref}
              spellCheck={spellCheck}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...inputBaseProps}
          />
          );

      return (maxLength && showCharCount)
        ? (
          <>
            {inputToDisplay}
            <CharacterCount
              maxLength={maxLength}
              currentLength={value?.length ?? 0}
            />
          </>
          ) : inputToDisplay;
    }}
  </InputBase>
));

export default TextInput;
